function formatDuration(ms) {
  if (ms == null || ms < 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}


function WalletIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M16 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" fill="currentColor" stroke="none" />
      <path d="M22 10V8a2 2 0 0 0-2-2H4" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      style={{ animation: "spin 0.9s linear infinite" }}
    >
      <path d="M12 2a10 10 0 1 0 10 10" opacity="0.3" />
      <path d="M22 12A10 10 0 0 0 12 2" />
    </svg>
  );
}

function CreateTokenButton({
  disabled = false,
  onClick = () => {},
  isConnected = false,
  isPolling = false,
  isSubmitting = false,
  isConnecting = false,
  vanityEnabled = false,
  progress = 0,
  durationMs,
  statusMessage,
  /** When connected and idle (e.g. Token-2022 “Submit revokes”) */
  idleLabel,
  /** When `isSubmitting` (overrides “Creating Token…” / vanity job text) */
  submittingLabel,
}) {
  const progressNum = isPolling
    ? Math.min(100, Math.max(0, Number(progress) || 0))
    : 100;
  const isActive = isPolling || isSubmitting || isConnecting;

  const buttonLabel =
    !isConnected
      ? "Connect Wallet"
      : isPolling
        ? "Processing..."
        : isSubmitting
          ? submittingLabel ??
            (vanityEnabled ? "Starting Mint Job..." : "Creating Token...")
          : isConnecting
            ? "Connecting..."
            : idleLabel ?? "Create SPL Token";
  const labelDisplay = buttonLabel || "Create SPL Token";

  const showIcon = isActive ? <SpinnerIcon /> : <WalletIcon size={18} />;

  return (
    <>
      <style>{`
        @keyframes particleRise {
          0% { transform: translateY(0) scale(1); opacity: 0.8; }
          80% { opacity: 0.4; }
          100% { transform: translateY(-48px) scale(0); opacity: 0; }
        }
        @keyframes orbPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes progressShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes statusFade {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes timerTick {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .btn-fill { transition: width 0.55s cubic-bezier(0.4, 0, 0.2, 1); }
        .btn-root:not(:disabled):hover .btn-shine { opacity: 1; }
        .btn-shine {
          pointer-events: none;
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.07) 50%, transparent 60%);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .btn-root:not(:disabled):active {
          transform: scale(0.985);
        }
        .btn-root { transition: box-shadow 0.25s, transform 0.12s; }
        .btn-root:not(:disabled):hover {
          box-shadow: 0 0 36px 4px rgba(34,211,238,0.22), 0 8px 32px rgba(0,0,0,0.4);
        }
      `}</style>

      <div style={{ width: "100%", fontFamily: "'DM Mono', 'Fira Mono', monospace" }}>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="btn-root"
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            borderRadius: "14px",
            padding: isPolling ? "14px 24px 12px" : "15px 28px",
            fontSize: "14px",
            fontWeight: "600",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#fff",
            border: disabled
              ? "1px solid rgba(148,163,184,0.3)"
              : "1px solid rgba(34,211,238,0.5)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.65 : 1,
            overflow: "hidden",
            background: "transparent",
            boxShadow: disabled
              ? "0 0 0 1px rgba(148,163,184,0.1), 0 4px 24px rgba(0,0,0,0.3)"
              : "0 0 0 1px rgba(34,211,238,0.15), 0 4px 24px rgba(0,0,0,0.35), 0 0 20px rgba(6,182,212,0.15)",
            outline: "none",
          }}
        >
          {/* Base: muted when disabled, dark when enabled */}
          <span
            style={{
              position: "absolute",
              inset: 0,
              background: disabled
                ? "linear-gradient(135deg, #334155 0%, #475569 100%)"
                : "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            }}
          />

          {/* Progress fill (full-width layer; percentage when polling) */}
          <span
            className="btn-fill"
            style={{
              position: "absolute",
              inset: "0 auto 0 0",
              width: isPolling ? `${progressNum}%` : "100%",
              background: disabled
                ? "linear-gradient(135deg, rgba(100,116,139,0.4) 0%, rgba(71,85,105,0.4) 100%)"
                : isPolling
                  ? "linear-gradient(90deg, #06b6d4 0%, #3b82f6 50%, #6366f1 100%)"
                  : "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 50%, #3b82f6 100%)",
              opacity: disabled ? 0.8 : 1,
              transition: isPolling
                ? "width 0.55s cubic-bezier(0.4,0,0.2,1)"
                : "none",
            }}
          />

          {/* Top edge highlight */}
          <span
            style={{
              position: "absolute",
              top: 0,
              left: "10%",
              right: "10%",
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(34,211,238,0.5), transparent)",
            }}
          />

          {/* Hover shine */}
          <span className="btn-shine" />

          {/* Content: message (when polling) + icon + label */}
          <span
            style={{
              position: "relative",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {isPolling && (statusMessage || durationMs != null) && (
              <span
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.08em",
                  color: "rgba(34,211,238,0.9)",
                  textTransform: "uppercase",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  textAlign: "center",
                }}
              >
                {statusMessage && <span>{statusMessage}</span>}
                {statusMessage && durationMs != null && " · "}
                {durationMs != null && (
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatDuration(durationMs)}
                  </span>
                )}
              </span>
            )}
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: isPolling ? "#22d3ee" : "rgba(255,255,255,0.95)",
                  transition: "color 0.3s",
                }}
              >
                {showIcon}
              </span>
              <span
                style={{
                  color: isPolling ? "#22d3ee" : "#ffffff",
                  fontWeight: 600,
                  transition: "color 0.3s",
                }}
              >
                {labelDisplay}
              </span>
            </span>
          </span>

          {/* Progress bar inside button at bottom (only when polling) */}
          {isPolling && (
            <span
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "rgba(15,23,42,0.85)",
                zIndex: 5,
              }}
            >
              <span
                className="btn-fill"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${progressNum}%`,
                  background: "linear-gradient(90deg, #06b6d4, #3b82f6)",
                  transition: "width 0.55s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </span>
          )}
        </button>
      </div>
    </>
  );
}

export default CreateTokenButton;